import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Icon from "../common/Icon.jsx";
import {
  formatVehicleLabel,
  listPublicBookingVehicles,
  listPublicTripTypes,
  seedTripTypes,
  seedBookingVehicles,
} from "../../services/masterDataService.js";
import { saveVehicleAvailabilityEnquiry } from "../../services/bookingService.js";
import { trackClick, trackBookRideSubmission } from "../../services/analyticsService.js";
import { getCurrentUser } from "../../services/authService.js";

const BUSINESS_WHATSAPP_NUMBER = "918320342657";

function getTodayDateString() {
  const d = new Date();
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export default function RideBookingForm({ variant = "widget", onBookingSubmitted, initialValues = {} }) {
  const [searchParams] = useSearchParams();
  const [tripTypes, setTripTypes] = useState(seedTripTypes);
  const [vehicles, setVehicles] = useState(seedBookingVehicles);
  const [loadingConfig, setLoadingConfig] = useState(false);
  const [configError, setConfigError] = useState("");

  const [form, setForm] = useState(() => {
    const paramTrip = searchParams.get("tripType") || searchParams.get("trip_type") || initialValues.tripTypeCode;
    let initialTripCode = "ONE_WAY";
    if (paramTrip) {
      const lower = paramTrip.toLowerCase();
      if (lower.includes("round") || lower.includes("return")) {
        initialTripCode = "ROUND_TRIP";
      } else if (lower.includes("one")) {
        initialTripCode = "ONE_WAY";
      } else {
        initialTripCode = paramTrip.toUpperCase();
      }
    }

    const paramVehicle = searchParams.get("vehicle") || searchParams.get("vehicleCode") || initialValues.vehicleCode;
    let initialVehicleCode = "ANY";
    if (paramVehicle) {
      initialVehicleCode = paramVehicle.toUpperCase();
    }

    return {
      fullName: searchParams.get("fullName") || searchParams.get("name") || initialValues.fullName || "",
      mobile: searchParams.get("mobile") || searchParams.get("phone") || initialValues.mobile || "",
      tripTypeCode: initialTripCode,
      vehicleCode: initialVehicleCode,
      pickupLocation: searchParams.get("pickupLocation") || initialValues.pickupLocation || "",
      dropLocation: searchParams.get("dropLocation") || initialValues.dropLocation || "",
      pickupDate: searchParams.get("pickupDate") || initialValues.pickupDate || getTodayDateString(),
      pickupTime: searchParams.get("pickupTime") || initialValues.pickupTime || "09:00",
      returnDate: searchParams.get("returnDate") || initialValues.returnDate || "",
      returnTime: searchParams.get("returnTime") || initialValues.returnTime || "18:00",
      passengers: Number(searchParams.get("passengers")) || initialValues.passengers || 2,
      requirements: searchParams.get("requirements") || initialValues.requirements || "",
    };
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(null);

  // Load active trip types and vehicles from database/master data API
  useEffect(() => {
    let isMounted = true;
    async function loadConfig() {
      try {
        const [tripResult, vehicleResult] = await Promise.all([
          listPublicTripTypes(),
          listPublicBookingVehicles(),
        ]);

        if (!isMounted) return;

        const activeTripTypes = tripResult.data || [];
        const activeVehicles = vehicleResult.data || [];

        if (activeTripTypes.length > 0) {
          setTripTypes(activeTripTypes);
          setForm((c) => {
            const matched = activeTripTypes.find(
              (t) => t.code?.toUpperCase() === c.tripTypeCode?.toUpperCase() || t.id === c.tripTypeCode
            );
            return {
              ...c,
              tripTypeCode: matched ? matched.code : activeTripTypes[0].code,
            };
          });
        }

        if (activeVehicles.length > 0) {
          setVehicles(activeVehicles);
          setForm((c) => {
            const matched = activeVehicles.find(
              (v) =>
                v.code?.toUpperCase() === c.vehicleCode?.toUpperCase() ||
                v.id === c.vehicleCode ||
                v.name?.toLowerCase().includes(c.vehicleCode?.toLowerCase())
            );
            return {
              ...c,
              vehicleCode: matched ? matched.code : activeVehicles[0].code,
            };
          });
        }
      } catch (e) {
        // Fallback seed data is already in state
      }
    }

    if (typeof window !== "undefined" && "requestIdleCallback" in window) {
      window.requestIdleCallback(() => loadConfig(), { timeout: 3500 });
    } else {
      setTimeout(loadConfig, 1000);
    }

    // Check if user is logged in to prefill contact info
    getCurrentUser().then(({ user }) => {
      if (!isMounted || !user) return;
      const userFull = user.user_metadata?.full_name || user.full_name || "";
      const userPhone = user.user_metadata?.mobile || user.mobile || "";
      setForm((c) => ({
        ...c,
        fullName: c.fullName || userFull,
        mobile: c.mobile || userPhone,
      }));
    });

    return () => {
      isMounted = false;
    };
  }, []);

  // Determine if selected trip type requires return journey
  const selectedTripType = tripTypes.find((t) => t.code === form.tripTypeCode);
  const requiresReturn = Boolean(selectedTripType?.requires_return_journey);

  // When switching to a trip type that does NOT require return, clear return date/time
  function handleTripTypeChange(newCode) {
    const nextType = tripTypes.find((t) => t.code === newCode);
    const nextRequiresReturn = Boolean(nextType?.requires_return_journey);

    setForm((current) => ({
      ...current,
      tripTypeCode: newCode,
      ...(nextRequiresReturn ? {} : { returnDate: "", returnTime: "" }),
    }));

    // Clear return field errors if now disabled
    if (!nextRequiresReturn) {
      setErrors((prev) => {
        const copy = { ...prev };
        delete copy.returnDate;
        delete copy.returnTime;
        return copy;
      });
    }
  }

  function updateField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    // Clear field-level error on change
    if (errors[field]) {
      setErrors((current) => {
        const copy = { ...current };
        delete copy[field];
        return copy;
      });
    }
  }

  function validate() {
    const nextErrors = {};

    // 1. Customer Name
    if (!form.fullName || !form.fullName.trim()) {
      nextErrors.fullName = "Please enter your name.";
    } else if (form.fullName.trim().length < 2) {
      nextErrors.fullName = "Name must be at least 2 characters.";
    }

    // 2. Customer Mobile Number
    const cleanPhone = String(form.mobile || "").replace(/\D/g, "");
    if (!cleanPhone) {
      nextErrors.mobile = "Please enter your 10-digit mobile number.";
    } else if (cleanPhone.length !== 10) {
      nextErrors.mobile = "Please enter a valid 10-digit Indian mobile number.";
    }

    // 3. Trip Type
    if (!form.tripTypeCode || !selectedTripType) {
      nextErrors.tripTypeCode = "Please select a valid trip type.";
    }

    // 4. Preferred Vehicle
    const selectedVehicle = vehicles.find((v) => v.code === form.vehicleCode);
    if (!form.vehicleCode || !selectedVehicle) {
      nextErrors.vehicleCode = "Please select a preferred vehicle.";
    }

    // 5. Pickup Location
    if (!form.pickupLocation || !form.pickupLocation.trim()) {
      nextErrors.pickupLocation = "Please enter pickup location.";
    }

    // 6. Drop Location
    if (!form.dropLocation || !form.dropLocation.trim()) {
      nextErrors.dropLocation = "Please enter drop location.";
    }

    // 7. Pickup Date & Time
    if (!form.pickupDate) {
      nextErrors.pickupDate = "Pickup date is required.";
    }
    if (!form.pickupTime) {
      nextErrors.pickupTime = "Pickup time is required.";
    }

    // 8. Return Date & Time (only for trip types that require return journey)
    if (requiresReturn) {
      if (!form.returnDate) {
        nextErrors.returnDate = "Return date is required for round trips.";
      } else if (form.pickupDate && form.returnDate < form.pickupDate) {
        nextErrors.returnDate = "Return date cannot be earlier than pickup date.";
      }

      if (!form.returnTime) {
        nextErrors.returnTime = "Return time is required for round trips.";
      } else if (
        form.pickupDate &&
        form.returnDate &&
        form.returnDate === form.pickupDate &&
        form.pickupTime &&
        form.returnTime <= form.pickupTime
      ) {
        nextErrors.returnTime = "Return time must be later than pickup time for same-day trips.";
      }
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (isSubmitting) return;

    if (!validate()) {
      return;
    }

    setIsSubmitting(true);

    const cleanMobile = form.mobile.replace(/\D/g, "");
    const tripTypeName = selectedTripType?.name || form.tripTypeCode;
    const selectedVehicleObj = vehicles.find((v) => v.code === form.vehicleCode);
    const vehicleLabel = formatVehicleLabel(selectedVehicleObj) || "Any";
    const reference = `TRI-${Math.random().toString(36).substring(2, 10).toUpperCase()}`;

    // Build WhatsApp enquiry message matching Section 6 template
    const messageLines = [
      "New Ride Enquiry — Trishuls",
      "",
      `Customer Name: ${form.fullName.trim()}`,
      `Mobile Number: ${cleanMobile}`,
      `Trip Type: ${tripTypeName}`,
      `Pickup Location: ${form.pickupLocation.trim()}`,
      `Drop Location: ${form.dropLocation.trim()}`,
      `Pickup Date: ${form.pickupDate}`,
      `Pickup Time: ${form.pickupTime}`,
      requiresReturn && form.returnDate ? `Return Date: ${form.returnDate}` : null,
      requiresReturn && form.returnTime ? `Return Time: ${form.returnTime}` : null,
      `Preferred Vehicle: ${vehicleLabel}`,
      form.passengers ? `Passenger Count: ${form.passengers}` : null,
      form.requirements && form.requirements.trim()
        ? `Additional Requirements: ${form.requirements.trim()}`
        : null,
      "",
      "Please share the best fare for this trip.",
    ].filter((line) => line !== null);

    const fullMessage = messageLines.join("\n");
    const waUrl = `https://wa.me/${BUSINESS_WHATSAPP_NUMBER}?text=${encodeURIComponent(
      fullMessage
    )}`;

    // Prepare canonical booking entry to display in Admin Panel
    const bookingPayload = {
      reference,
      customer_name: form.fullName.trim(),
      mobile_number: cleanMobile,
      trip_type: form.tripTypeCode,
      rental_type: "with_driver",
      pickup_location: form.pickupLocation.trim(),
      drop_location: form.dropLocation.trim(),
      pickup_date: form.pickupDate,
      pickup_time: form.pickupTime,
      return_date: requiresReturn ? form.returnDate : null,
      return_time: requiresReturn ? form.returnTime : null,
      vehicle_name: vehicleLabel,
      passengers: Number(form.passengers) || 1,
      requirements: [
        `Vehicle: ${vehicleLabel}`,
        `Trip Type: ${tripTypeName}`,
        form.requirements ? `Requirements: ${form.requirements.trim()}` : "",
        `Enquiry via Website (${variant}) - WhatsApp Direct`,
      ]
        .filter(Boolean)
        .join("\n"),
      status: "pending",
      notes: "Enquiry submitted via GET BEST FARE - WhatsApp redirected.",
    };

    try {
      await saveVehicleAvailabilityEnquiry(bookingPayload);
      trackBookRideSubmission(bookingPayload);
      trackClick("whatsapp_click", {
        source: `booking_form_${variant}`,
        reference,
        trip_type: form.tripTypeCode,
        vehicle: vehicleLabel,
      });
    } catch (err) {
      console.warn("Could not persist enquiry:", err);
    }

    setSubmitSuccess({
      reference,
      waUrl,
      customerName: form.fullName.trim(),
      tripTypeName,
      vehicleLabel,
    });
    setIsSubmitting(false);

    if (onBookingSubmitted) {
      onBookingSubmitted(bookingPayload);
    }

    // Open WhatsApp in a new tab
    const waWindow = window.open(waUrl, "_blank", "noopener,noreferrer");
    if (!waWindow || waWindow.closed || typeof waWindow.closed === "undefined") {
      // Fallback if browser popup blocker intervened
      window.location.href = waUrl;
    }
  }

  function handleReset() {
    setSubmitSuccess(null);
    setErrors({});
  }

  if (loadingConfig && tripTypes.length === 0) {
    return (
      <div style={{ padding: "28px", textAlign: "center", color: "var(--muted)" }}>
        <Icon name="ic-car" size={24} />
        <div style={{ marginTop: "10px", fontSize: "0.95rem" }}>
          Loading journey options...
        </div>
      </div>
    );
  }

  return (
    <form
      id={`ride-booking-form-${variant}`}
      name="ride_booking_form"
      className="booking-card ride-booking-form"
      onSubmit={handleSubmit}
      noValidate
      aria-label="Ride Booking and Fare Enquiry Form"
      data-webmcp-tool="trishul_ride_booking"
      data-webmcp-description="Book a ride or get best fare with Trishul Tours & Travels"
    >
      <div style={{ marginBottom: "14px" }}>
        <h2 style={{ fontSize: "1.18rem", margin: "0 0 4px", display: "flex", alignItems: "center", gap: "8px" }}>
          <Icon name="ic-car" size={20} style={{ color: "var(--gold-500)" }} />
          Get Instant Fare &amp; Book Your Ride
        </h2>
        <p style={{ margin: 0, fontSize: "0.86rem", color: "var(--muted-lt, #94a3b8)" }}>
          Choose your journey details and preferred vehicle for the best price
        </p>
      </div>

      {configError && (
        <div
          role="alert"
          style={{
            padding: "10px 14px",
            marginBottom: "14px",
            borderRadius: "8px",
            background: "rgba(239, 68, 68, 0.12)",
            color: "#f87171",
            fontSize: "0.85rem",
          }}
        >
          {configError}
        </div>
      )}

      <div className="compact-form-grid">
        {/* Trip Type (Tab-like Structure as before) */}
        <div className="field full">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "8px" }}>
            <span
              id={`label-triptype-${variant}`}
              style={{
                fontSize: "0.85rem",
                fontWeight: "700",
                color: "var(--text-label)",
              }}
            >
              Select Trip Type *
            </span>
            <span style={{ fontSize: "0.76rem", color: "var(--gold-text)", fontWeight: "600" }}>
              {requiresReturn ? "Round trip includes return journey" : "One-way point-to-point journey"}
            </span>
          </div>

          <div
            className="trip-tabs"
            role="tablist"
            aria-labelledby={`label-triptype-${variant}`}
            style={{
              display: "grid",
              gridTemplateColumns: tripTypes.length <= 3 ? `repeat(${tripTypes.length}, minmax(0, 1fr))` : "repeat(auto-fit, minmax(130px, 1fr))",
              gap: "10px",
              marginBottom: "8px",
            }}
          >
            {tripTypes.map((type) => {
              const isSelected = form.tripTypeCode === (type.code || type.id);
              const iconName = type.icon || (type.requires_return_journey ? "ic-route" : "ic-arrow-right");
              return (
                <button
                  key={type.code || type.id}
                  type="button"
                  role="tab"
                  aria-selected={isSelected}
                  className={isSelected ? "active" : ""}
                  onClick={() => handleTripTypeChange(type.code || type.id)}
                >
                  <Icon name={iconName} size={18} />
                  <span>{type.name}</span>
                </button>
              );
            })}
          </div>

          {/* Accessible select for screen readers and WebMCP tools */}
          <div className="sr-only">
            <label htmlFor={`form-tripType-${variant}`}>Trip Type *</label>
            <select
              id={`form-tripType-${variant}`}
              name="tripType"
              required
              aria-label="Trip Type"
              data-webmcp-input="trip_type"
              value={form.tripTypeCode}
              onChange={(e) => handleTripTypeChange(e.target.value)}
            >
              {tripTypes.map((type) => (
                <option key={type.code || type.id} value={type.code || type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>
          {errors.tripTypeCode && (
            <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
              {errors.tripTypeCode}
            </span>
          )}
        </div>

        {/* Pickup Location */}
        <div className="field">
          <label htmlFor={`form-pickupLoc-${variant}`}>Pickup Location *</label>
          <input
            id={`form-pickupLoc-${variant}`}
            name="pickupLocation"
            type="text"
            required
            placeholder="e.g. Vapi Railway Station"
            aria-label="Pickup Location"
            data-webmcp-input="pickup_location"
            value={form.pickupLocation}
            onChange={(e) => updateField("pickupLocation", e.target.value)}
            style={errors.pickupLocation ? { borderColor: "#ef4444" } : {}}
          />
          {errors.pickupLocation && (
            <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
              {errors.pickupLocation}
            </span>
          )}
        </div>

        {/* Drop Location */}
        <div className="field">
          <label htmlFor={`form-dropLoc-${variant}`}>Drop Location *</label>
          <input
            id={`form-dropLoc-${variant}`}
            name="dropLocation"
            type="text"
            required
            placeholder="e.g. Mumbai Airport T2"
            aria-label="Drop Location"
            data-webmcp-input="drop_location"
            value={form.dropLocation}
            onChange={(e) => updateField("dropLocation", e.target.value)}
            style={errors.dropLocation ? { borderColor: "#ef4444" } : {}}
          />
          {errors.dropLocation && (
            <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
              {errors.dropLocation}
            </span>
          )}
        </div>

        {/* Pickup Date (Visible Label) */}
        <div className="field">
          <label htmlFor={`form-pickupDate-${variant}`}>
            Pickup Date *
          </label>
          <input
            id={`form-pickupDate-${variant}`}
            name="pickupDate"
            type="date"
            required
            min={getTodayDateString()}
            value={form.pickupDate}
            onChange={(e) => updateField("pickupDate", e.target.value)}
            style={errors.pickupDate ? { borderColor: "#ef4444" } : {}}
            aria-label="Pickup Date"
            data-webmcp-input="pickup_date"
          />
          {errors.pickupDate && (
            <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
              {errors.pickupDate}
            </span>
          )}
        </div>

        {/* Pickup Time (Visible Label) */}
        <div className="field">
          <label htmlFor={`form-pickupTime-${variant}`}>
            Pickup Time *
          </label>
          <input
            id={`form-pickupTime-${variant}`}
            name="pickupTime"
            type="time"
            required
            value={form.pickupTime}
            onChange={(e) => updateField("pickupTime", e.target.value)}
            style={errors.pickupTime ? { borderColor: "#ef4444" } : {}}
            aria-label="Pickup Time"
            data-webmcp-input="pickup_time"
          />
          {errors.pickupTime && (
            <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
              {errors.pickupTime}
            </span>
          )}
        </div>

        {/* Return Date & Time (Only visible when Round / Return Trip is selected) */}
        {requiresReturn && (
          <>
            <div className="field">
              <label htmlFor={`form-returnDate-${variant}`}>Return Date *</label>
              <input
                id={`form-returnDate-${variant}`}
                name="returnDate"
                type="date"
                min={form.pickupDate || getTodayDateString()}
                required
                value={form.returnDate}
                onChange={(e) => updateField("returnDate", e.target.value)}
                style={errors.returnDate ? { borderColor: "#ef4444" } : {}}
                aria-label="Return Date"
                data-webmcp-input="return_date"
              />
              {errors.returnDate && (
                <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
                  {errors.returnDate}
                </span>
              )}
            </div>

            <div className="field">
              <label htmlFor={`form-returnTime-${variant}`}>Return Time *</label>
              <input
                id={`form-returnTime-${variant}`}
                name="returnTime"
                type="time"
                required
                value={form.returnTime}
                onChange={(e) => updateField("returnTime", e.target.value)}
                style={errors.returnTime ? { borderColor: "#ef4444" } : {}}
                aria-label="Return Time"
                data-webmcp-input="return_time"
              />
              {errors.returnTime && (
                <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
                  {errors.returnTime}
                </span>
              )}
            </div>
          </>
        )}

        {/* Preferred Vehicle (Dropdown) */}
        <div className="field">
          <label htmlFor={`form-vehicle-${variant}`}>Preferred Vehicle *</label>
          <select
            id={`form-vehicle-${variant}`}
            name="vehicleCode"
            required
            aria-label="Preferred Vehicle"
            data-webmcp-input="preferred_vehicle"
            value={form.vehicleCode}
            onChange={(e) => updateField("vehicleCode", e.target.value)}
            style={errors.vehicleCode ? { borderColor: "#ef4444" } : {}}
          >
            {vehicles.map((veh) => (
              <option key={veh.code || veh.id} value={veh.code || veh.id}>
                {formatVehicleLabel(veh)}
              </option>
            ))}
          </select>
          {errors.vehicleCode && (
            <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
              {errors.vehicleCode}
            </span>
          )}
        </div>

        {/* Passenger Count */}
        <div className="field">
          <label htmlFor={`form-passengers-${variant}`}>Passenger Count</label>
          <input
            id={`form-passengers-${variant}`}
            name="passengers"
            type="number"
            min="1"
            max="50"
            aria-label="Passenger Count"
            data-webmcp-input="passenger_count"
            value={form.passengers}
            onChange={(e) => updateField("passengers", e.target.value)}
          />
        </div>

        {/* Full Name */}
        <div className="field">
          <label htmlFor={`form-fullName-${variant}`}>Customer Name *</label>
          <input
            id={`form-fullName-${variant}`}
            name="fullName"
            type="text"
            required
            placeholder="Enter your full name"
            aria-label="Customer Name"
            data-webmcp-input="full_name"
            value={form.fullName}
            onChange={(e) => updateField("fullName", e.target.value)}
            style={errors.fullName ? { borderColor: "#ef4444" } : {}}
          />
          {errors.fullName && (
            <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
              {errors.fullName}
            </span>
          )}
        </div>

        {/* Mobile Number */}
        <div className="field">
          <label htmlFor={`form-mobile-${variant}`}>Mobile Number *</label>
          <input
            id={`form-mobile-${variant}`}
            name="mobile"
            type="tel"
            required
            pattern="[0-9]{10}"
            maxLength={10}
            placeholder="10-digit mobile number"
            aria-label="10-digit Mobile Number"
            data-webmcp-input="mobile_number"
            value={form.mobile}
            onChange={(e) => updateField("mobile", e.target.value)}
            style={errors.mobile ? { borderColor: "#ef4444" } : {}}
          />
          {errors.mobile && (
            <span role="alert" style={{ color: "#ef4444", fontSize: "0.78rem", marginTop: "3px", display: "block" }}>
              {errors.mobile}
            </span>
          )}
        </div>

        {/* Additional Requirements */}
        <div className="field full">
          <label htmlFor={`form-requirements-${variant}`}>Additional Requirements (Optional)</label>
          <input
            id={`form-requirements-${variant}`}
            name="requirements"
            type="text"
            placeholder="Child seat, luggage space, flight timing, multiple stops..."
            aria-label="Additional Requirements"
            data-webmcp-input="requirements"
            value={form.requirements}
            onChange={(e) => updateField("requirements", e.target.value)}
          />
        </div>
      </div>

      {/* Primary Action Button: EXACTLY "GET BEST FARE" */}
      <button
        id={`btn-get-best-fare-${variant}`}
        className="btn btn-gold btn-block"
        type="submit"
        disabled={isSubmitting}
        aria-label="Get Best Fare on WhatsApp"
        data-webmcp-action="submit"
        style={{
          marginTop: "16px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "8px",
          fontSize: "1rem",
          fontWeight: "700",
          letterSpacing: "0.03em",
          padding: "14px 20px",
        }}
      >
        <Icon name="ic-whatsapp" size={18} />
        {isSubmitting ? "PREPARING BEST FARE..." : "GET BEST FARE"}
      </button>

      {/* Confirmation feedback alert */}
      {submitSuccess && (
        <div
          className="booking-success-alert"
          style={{
            marginTop: "16px",
            padding: "14px 16px",
            borderRadius: "12px",
            background: "rgba(16, 185, 129, 0.12)",
            border: "1px solid rgba(16, 185, 129, 0.35)",
            color: "#10b981",
            fontSize: "0.88rem",
            lineHeight: "1.45",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "8px", fontWeight: "600", marginBottom: "4px" }}>
            <Icon name="ic-check-shield" size={16} />
            Enquiry Generated — Ref: {submitSuccess.reference}
          </div>
          <div style={{ color: "var(--text-light, #e2e8f0)", fontSize: "0.84rem" }}>
            We've opened WhatsApp with your journey details ({submitSuccess.tripTypeName} • {submitSuccess.vehicleLabel}).
            {" "}Please click send on WhatsApp to receive the lowest quotation!
          </div>
          <div style={{ marginTop: "10px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <a
              href={submitSuccess.waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-sm"
              style={{
                background: "#25D366",
                color: "#fff",
                display: "inline-flex",
                alignItems: "center",
                gap: "6px",
                padding: "6px 14px",
                fontSize: "0.82rem",
                borderRadius: "6px",
                textDecoration: "none",
                fontWeight: "600",
              }}
            >
              <Icon name="ic-whatsapp" size={14} /> Open WhatsApp Chat Again
            </a>
            <button
              type="button"
              className="btn btn-ghost btn-sm"
              onClick={handleReset}
              style={{ fontSize: "0.82rem", padding: "6px 12px" }}
            >
              New Enquiry
            </button>
          </div>
        </div>
      )}
    </form>
  );
}
